import React, { useEffect, useRef, useState } from 'react';
import styles from './ResearchFilters.module.css';
import {
  RESEARCH_STATUS,
  RESEARCH_STATUS_LABELS,
  EARLIEST_PUBLICATION_YEAR,
  latestPublicationYear,
  isCompletePublicationYear,
} from '../../../app/helpers/researchQuery';
import useDebouncedValue from '../../../Hooks/useDebouncedValue';

/**
 * =============================================================================
 * ResearchFilters — the toolbar over the research registry
 * =============================================================================
 *
 * From the October review: filters beyond search, a count that answers to
 * them, bulk selection, and export options.
 *
 * WHY IT LOOKS LIKE THIS
 *
 * The first version put every control on one wrapping line with its label
 * inline — «من تاريخ» then a date box, «سنة النشر من» then a number box — and
 * it read as a jumble: six controls of five different widths, two orphaned
 * rows beneath, and no way to tell at a glance whether anything was filtered.
 *
 * What changed, and why each one matters to someone using this daily:
 *
 *  - LABELS SIT ABOVE THEIR CONTROL, so every field is the same shape and the
 *    eye can scan the row. Inline labels make each field a different width,
 *    which is what produced the staircase.
 *  - RANGES ARE ONE FIELD, not two. «تاريخ التقديم» is a single labelled unit
 *    containing from and to, because that is how an operator thinks about it.
 *  - ONE GRID, `auto-fit` with a floor, so the same markup is four columns on
 *    a wide screen and one on a narrow one with no breakpoint guesswork.
 *  - THE COUNT AND THE EXPORT SIT TOGETHER at the end, because the count is
 *    what tells you what the export is about to contain.
 *  - A BADGE COUNTS ACTIVE FILTERS. Without it, a narrowed list and an empty
 *    one look identical, and "why can't I find it" is usually a filter
 *    somebody forgot they set.
 *
 * Every control maps to a query parameter the endpoint accepts. Nothing is
 * offered that the API cannot honour — a filter that quietly does nothing is
 * worse than no filter, because the operator believes the result.
 *
 * ⚠ AND IT COLLAPSES, WHICH IS THE POINT.
 *
 * Open, this panel is five fields over two rows. Above a table showing four
 * records that was roughly 400px of chrome before the first row of actual
 * data — the screen was mostly toolbar. THE TABLE IS THE PAGE; the filters are
 * something you reach for occasionally and then stop thinking about.
 *
 * So the default is one row: the toggle, whatever is currently narrowing the
 * list, the count, and the export. Everything an operator needs to KNOW stays
 * visible; only the controls they need to CHANGE it are folded away. The badge
 * on the toggle and the chips beside it mean a filtered list can never be
 * mistaken for an empty one while the panel is shut.
 */

const KIND_OPTIONS = [
  { value: '', label: 'الكل' },
  { value: 'research', label: 'بحث علمي' },
  { value: 'study', label: 'دراسة' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'الكل' },
  { value: RESEARCH_STATUS.NEW, label: RESEARCH_STATUS_LABELS.new },
  { value: RESEARCH_STATUS.READ, label: RESEARCH_STATUS_LABELS.read },
  { value: RESEARCH_STATUS.ARCHIVED, label: RESEARCH_STATUS_LABELS.archived },
];

const AFFILIATION_OPTIONS = [
  { value: '', label: 'الكل' },
  { value: 'IsDubaiPoliceEmployee', label: 'من موظفي شرطة دبي' },
  { value: 'IsFacultyMember', label: 'عضو هيئة تدريس' },
  { value: 'IsAcademyStudent', label: 'طالب في الأكاديمية' },
];

const CURRENT_YEAR = latestPublicationYear();
const EARLIEST_YEAR = EARLIEST_PUBLICATION_YEAR;

/**
 * How long the year boxes stay quiet before they ask the API anything.
 *
 * 450ms is a deliberate choice, not a round number: below ~300ms a two-finger
 * typist still trips a request mid-word, and above ~600ms the screen starts to
 * feel like it has stopped responding. Four digits typed at a normal pace fall
 * comfortably inside it, so «2026» produces exactly one request.
 */
const YEAR_COMMIT_DELAY = 450;

/** Digits only, four at most — anything else cannot become a year. */
const toYearDigits = (text) => String(text ?? '').replace(/[^\d]/g, '').slice(0, 4);

/**
 * Label above control. One shape for every field in the grid.
 *
 * `hint` is reserved for the one thing a filter row genuinely needs to say:
 * «this is not applied yet, and here is why». It renders in the row's own
 * flow so an appearing message cannot push the grid around.
 */
const Field = ({ label, htmlFor, wide, hint, children }) => (
  <div className={`${styles.field} ${wide ? styles.wide : ''}`}>
    <label className={styles.label} htmlFor={htmlFor}>
      {label}
    </label>
    {children}
    <span className={styles.hint} role={hint ? 'status' : undefined}>
      {hint || ''}
    </span>
  </div>
);

const ResearchFilters = ({
  filters,
  onChange,
  onReset,
  total,
  isLoading,
  selectedCount = 0,
  onClearSelection,
  includeAttachments,
  onToggleAttachments,
  onExport,
}) => {
  const affiliation =
    AFFILIATION_OPTIONS.slice(1).find((o) => filters[o.value] === 'true')
      ?.value || '';

  const set = (key, value) => onChange({ ...filters, [key]: value });

  const setAffiliation = (value) => {
    // Only one affiliation flag is ever sent; the others are cleared so a
    // previous choice cannot survive invisibly in the query string.
    onChange({
      ...filters,
      IsDubaiPoliceEmployee: '',
      IsFacultyMember: '',
      IsAcademyStudent: '',
      ...(value ? { [value]: 'true' } : {}),
    });
  };

  /**
   * ===========================================================================
   * THE YEAR BOXES TYPE LOCALLY AND COMMIT WHEN THEY ARE READY
   * ===========================================================================
   *
   * WHAT WAS WRONG. Both year inputs called `set(...)` straight out of
   * `onChange`, and every `set` is a new query string, a new request. Typing
   * «2026» sent `PublicationYearFrom=2`, then `=20`, then `=202`, then
   * `=2026` — and the API rejected the first three with HTTP 400, because 2 is
   * not a year. The operator typed one correct value and watched three failures.
   *
   * It was worse than noisy. Each of those four responses raced the others, so
   * the table could settle on whichever landed last rather than on the one that
   * matched what was on screen.
   *
   * HOW IT WORKS NOW. The boxes own what is typed. That draft is published to
   * the page once typing has been quiet for `YEAR_COMMIT_DELAY`, and only the
   * halves that ARE years — four digits inside the allowed range, in order.
   * Everything else stays local, says why in the row's hint, and sends nothing.
   */
  const [yearDraft, setYearDraft] = useState({
    from: filters.PublicationYearFrom || '',
    to: filters.PublicationYearTo || '',
  });

  /**
   * What THIS component last pushed upward.
   *
   * Needed to tell our own echo apart from a change made somewhere else. The
   * sync effect below pulls the draft down whenever `filters` disagrees with
   * the boxes — and without this it could not distinguish «مسح التصفية»
   * (clear the boxes, yes) from our own refusal to publish an out-of-order
   * range (leave the boxes alone, the operator is mid-correction). Measured:
   * without it, typing «من 2024 إلى 2020» emptied both boxes under the
   * operator's cursor.
   */
  const publishedYears = useRef({
    from: filters.PublicationYearFrom || '',
    to: filters.PublicationYearTo || '',
  });

  /**
   * Which halves of the draft are usable, and whether the pair makes sense.
   *
   * THE ORDER CHECK ONLY APPLIES WHEN BOTH HALVES ARE REAL YEARS. A naive
   * `Number(from) > Number(to)` treats a half-typed «2» as the year 2, so
   * «من 2020» plus one keystroke of «إلى» read as 2020 > 2 and threw away
   * the complete half the operator had already finished.
   */
  const fromOk = isCompletePublicationYear(yearDraft.from);
  const toOk = isCompletePublicationYear(yearDraft.to);
  const outOfOrder =
    fromOk && toOk && Number(yearDraft.from) > Number(yearDraft.to);

  /**
   * What to say about a draft that is not a filter yet.
   *
   * Computed from the DRAFT, so the hint tracks what the operator can see
   * rather than what was last sent.
   */
  const yearHint = (() => {
    if ((yearDraft.from && !fromOk) || (yearDraft.to && !toOk)) {
      // Named rather than generic: «أدخل سنة صحيحة» leaves the operator
      // to guess whether the problem is the digits or the range.
      return `أدخل سنة من أربعة أرقام بين ${EARLIEST_YEAR} و ${CURRENT_YEAR}`;
    }
    if (outOfOrder) return 'سنة البداية يجب ألا تتجاوز سنة النهاية';
    return '';
  })();

  /**
   * Publish a draft — keeping only the halves that are real years.
   *
   * A half still being typed is published as `''`, which `buildResearchQuery`
   * omits. So an in-progress range narrows the list by the half that IS
   * complete instead of erroring on the half that is not.
   */
  const commitYears = (draft) => {
    const okFrom = isCompletePublicationYear(draft.from);
    const okTo = isCompletePublicationYear(draft.to);
    const reversed = okFrom && okTo && Number(draft.from) > Number(draft.to);

    const nextFrom = okFrom && !reversed ? draft.from : '';
    const nextTo = okTo && !reversed ? draft.to : '';

    const currentFrom = filters.PublicationYearFrom || '';
    const currentTo = filters.PublicationYearTo || '';

    // Nothing to say: re-publishing an unchanged value would reset the page
    // and drop the operator's row selection for no reason.
    if (nextFrom === currentFrom && nextTo === currentTo) return;

    publishedYears.current = { from: nextFrom, to: nextTo };
    onChange({
      ...filters,
      PublicationYearFrom: nextFrom,
      PublicationYearTo: nextTo,
    });
  };

  /** The draft waiting on the timer, or null when nothing is pending. */
  const [pendingYears, setPendingYears] = useState(null);

  /**
   * A keystroke. Debounced — EXCEPT emptying a box, which commits at once.
   *
   * Taking a filter off is a decision, not typing, and making someone wait
   * 450ms to watch a filter come off is the kind of lag that reads as a broken
   * screen. The debounce exists to stop half-typed values reaching the API;
   * an empty box is not a half-typed value.
   */
  const handleYearInput = (half, raw) => {
    const next = { ...yearDraft, [half]: toYearDigits(raw) };
    setYearDraft(next);
    setPendingYears(next[half] === '' ? null : next);
    if (next[half] === '') commitYears(next);
  };

  /**
   * Debounced as one object rather than two values: the pair is a single idea,
   * and committing the halves separately would fire a request for a range the
   * operator was still halfway through describing.
   *
   * The key is a primitive so the debounce compares by value — an object
   * literal is a new reference on every render and would never settle.
   */
  const settledYearKey = useDebouncedValue(
    pendingYears ? `${pendingYears.from}|${pendingYears.to}` : '',
    YEAR_COMMIT_DELAY
  );

  useEffect(() => {
    if (!settledYearKey) return;
    const [from, to] = settledYearKey.split('|');
    commitYears({ from, to });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settledYearKey]);

  /**
   * Follow `filters` when something ELSE changes it — «مسح التصفية»
   * above all. A year left sitting in a box that is no longer filtering
   * anything is the worst state this screen can be in.
   *
   * Our own echo is ignored (see `publishedYears`), so refusing to publish an
   * out-of-order range does not yank the boxes out from under the operator.
   */
  useEffect(() => {
    const publishedFrom = filters.PublicationYearFrom || '';
    const publishedTo = filters.PublicationYearTo || '';
    if (
      publishedFrom === publishedYears.current.from &&
      publishedTo === publishedYears.current.to
    ) {
      return;
    }
    publishedYears.current = { from: publishedFrom, to: publishedTo };
    setPendingYears(null);
    setYearDraft({ from: publishedFrom, to: publishedTo });
  }, [filters.PublicationYearFrom, filters.PublicationYearTo]);

  // Sort is not a filter the operator set, so it must not count towards the
  // badge or light up the reset control.
  const activeCount = Object.entries(filters).filter(
    ([key, value]) => value && key !== 'SortBy' && key !== 'SortDirection'
  ).length;

  const [open, setOpen] = useState(false);

  return (
    <section className={styles.panel} aria-label="عوامل التصفية">
      <div className={styles.bar}>
        <button
          type="button"
          className={`${styles.disclosure} ${open ? styles.disclosureOpen : ''}`}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls="research-filter-grid"
        >
          <span aria-hidden="true" className={styles.filterIcon}>
            ⚲
          </span>
          تصفية
          {activeCount ? (
            <span className={styles.badge}>{activeCount}</span>
          ) : null}
          <span aria-hidden="true" className={styles.caret}>
            ▾
          </span>
        </button>

        <div className={styles.status} aria-live="polite">
          {isLoading ? (
            <span className={styles.muted}>…جارٍ التحميل</span>
          ) : (
            <>
              <span className={styles.count}>
                <strong>{Number.isFinite(total) ? total : 0}</strong>{' '}
                <span className={styles.muted}>
                  {activeCount ? 'نتيجة مطابقة' : 'سجل'}
                </span>
              </span>

              {/* Visible whether the panel is open or shut: with it closed,
                  this is the only thing distinguishing a filtered list from an
                  empty one. */}
              {activeCount ? (
                <button
                  type="button"
                  className={styles.chip}
                  onClick={onReset}
                  title="مسح عوامل التصفية"
                >
                  مسح التصفية
                  <span aria-hidden="true" className={styles.chipX}>
                    ×
                  </span>
                </button>
              ) : null}

              {selectedCount > 0 ? (
                <button
                  type="button"
                  className={`${styles.chip} ${styles.chipSelected}`}
                  onClick={onClearSelection}
                  title="إلغاء التحديد"
                >
                  {selectedCount} محدد
                  <span aria-hidden="true" className={styles.chipX}>
                    ×
                  </span>
                </button>
              ) : null}
            </>
          )}
        </div>

        <div className={styles.actions}>
          {/* The switch sits WITH the export it changes, not in a settings
              menu: it alters what the next click produces. */}
          <label className={styles.toggle}>
            <input
              type="checkbox"
              checked={Boolean(includeAttachments)}
              onChange={(e) => onToggleAttachments?.(e.target.checked)}
            />
            <span>تضمين المرفقات</span>
          </label>

          <button type="button" className={styles.export} onClick={onExport}>
            {selectedCount > 0
              ? `تصدير المحدد (${selectedCount})`
              : 'تصدير الكل'}
          </button>
        </div>
      </div>

      <div
        id="research-filter-grid"
        className={styles.grid}
        hidden={!open}
      >
        <Field label="نوع التسجيل" htmlFor="f-kind">
          <select
            id="f-kind"
            className={styles.control}
            value={filters.SubmissionKind || ''}
            onChange={(e) => set('SubmissionKind', e.target.value)}
          >
            {KIND_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="الحالة" htmlFor="f-status">
          <select
            id="f-status"
            className={styles.control}
            value={filters.Status || ''}
            onChange={(e) => set('Status', e.target.value)}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="صفة مقدم الطلب" htmlFor="f-affiliation">
          <select
            id="f-affiliation"
            className={styles.control}
            value={affiliation}
            onChange={(e) => setAffiliation(e.target.value)}
          >
            {AFFILIATION_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>

        {/* A range is ONE field. Two separately-labelled boxes make the reader
            assemble the idea themselves every time they look at it. */}
        <Field label="تاريخ التقديم" wide>
          <div className={styles.range}>
            <input
              type="date"
              className={styles.control}
              value={filters.SubmittedFrom || ''}
              onChange={(e) => set('SubmittedFrom', e.target.value)}
              // The browser enforces the order, so the operator cannot submit
              // a range that can only ever return nothing.
              max={filters.SubmittedTo || undefined}
              aria-label="تاريخ التقديم من"
            />
            <span className={styles.rangeDash} aria-hidden="true">
              —
            </span>
            <input
              type="date"
              className={styles.control}
              value={filters.SubmittedTo || ''}
              onChange={(e) => set('SubmittedTo', e.target.value)}
              min={filters.SubmittedFrom || undefined}
              aria-label="تاريخ التقديم إلى"
            />
          </div>
        </Field>

        {/* `type="text"` with `inputMode="numeric"`, NOT `type="number"`.
            A number input hands back `''` for anything it considers partially
            invalid — so «202e» arrives as an empty string and the field looks
            cleared while still showing text. It also accepts `e`, `+`, `-` and
            a decimal point, none of which belong in a year, and its spinner
            arrows are a 1px target that changes a filter by accident. Digits
            are filtered on the way in instead, and the numeric keypad still
            comes up on a tablet. */}
        <Field label="سنة النشر" wide hint={yearHint}>
          <div
            className={`${styles.range} ${yearHint ? styles.rangeInvalid : ''}`}
          >
            <input
              type="text"
              inputMode="numeric"
              autoComplete="off"
              maxLength={4}
              className={styles.control}
              value={yearDraft.from}
              onChange={(e) => handleYearInput('from', e.target.value)}
              placeholder={String(EARLIEST_YEAR)}
              aria-label="سنة النشر من"
              aria-invalid={Boolean(yearHint)}
            />
            <span className={styles.rangeDash} aria-hidden="true">
              —
            </span>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="off"
              maxLength={4}
              className={styles.control}
              value={yearDraft.to}
              onChange={(e) => handleYearInput('to', e.target.value)}
              placeholder={String(CURRENT_YEAR)}
              aria-label="سنة النشر إلى"
              aria-invalid={Boolean(yearHint)}
            />
          </div>
        </Field>
      </div>

    </section>
  );
};

export default ResearchFilters;
export { KIND_OPTIONS, STATUS_OPTIONS, AFFILIATION_OPTIONS };
